import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Link as MuiLink } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';

const ContactUs = () => {
    const navigate = useNavigate();

    const breadcrumbItems = [
        { title: 'Home', path: '/' },
        { title: 'Contact Us', path: '/contact-us' },
    ];

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [emailError, setEmailError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Email validation
    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setEmailError('');
    };

    const handleEmailBlur = () => {
        if (email && !validateEmail(email)) {
            setEmailError('Please enter a valid email address');
        }
    };

    const isFormValid =
        firstName.trim() !== '' &&
        lastName.trim() !== '' &&
        email.trim() !== '' &&
        message.trim() !== '' &&
        validateEmail(email);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isFormValid) {
            setSubmitted(true);
        }
    };

    return (
        <>
            <Navbar />
            <HeroBanner
                heroTitle="Contact Us"
                heroDescription="Get in touch with the BIDT team"
                imageUrl="/images/digiTwins.png"
                height="165px"
            />
            <Breadcrumb items={breadcrumbItems} />

            <Box sx={{ maxWidth: '1300px', margin: '32px auto', px: 2 }}>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                    {/* Form Section 60% */}
                    <Box sx={{ flex: '0 0 60%' }}>
                        {!submitted ? (
                            <>
                                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '24px', fontWeight: 400, color: '#142434', mb: 3 }}>
                                    Contact Us
                                </Typography>

                                {/* First Name */}
                                <Box mb={2}>
                                    <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14, mb: 0.5 }}>First Name</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        inputProps={{ style: { fontFamily: 'PT Sans', fontSize: 14 } }}
                                    />
                                </Box>

                                {/* Last Name */}
                                <Box mb={2}>
                                    <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14, mb: 0.5 }}>Last Name</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        inputProps={{ style: { fontFamily: 'PT Sans', fontSize: 14 } }}
                                    />
                                </Box>

                                {/* Email */}
                                <Box mb={2}>
                                    <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14, mb: 0.5 }}>Email</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        value={email}
                                        onChange={handleEmailChange}
                                        onBlur={handleEmailBlur}
                                        inputProps={{ style: { fontFamily: 'PT Sans', fontSize: 14 } }}
                                    />
                                </Box>

                                {/* Message */}
                                <Box mb={2}>
                                    <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14, mb: 0.5 }}>Message</Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={5}
                                        size="small"
                                        variant="outlined"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        inputProps={{ style: { fontFamily: 'PT Sans', fontSize: 14 } }}
                                    />
                                </Box>

                                {/* Reserved space for error messages */}
                                <Box sx={{ minHeight: '24px', mb: 2 }}>
                                    {emailError && (
                                        <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14, color: 'error.main' }}>
                                            {emailError}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Submit Button */}
                                <Button
  variant="contained"
  disabled={!isFormValid}
  onClick={handleSubmit}
  sx={{
    width: '190px',
    height: '40px', // match LoginModal
    fontFamily: 'Poppins, sans-serif',
    fontSize: '18px',
    fontWeight: 600,
    color: '#FFFFFF',
    boxShadow: 'none',
    backgroundColor: isFormValid ? '#489596' : '#999C9F', // enabled/disabled colors
    '&:hover': {
      backgroundColor: isFormValid ? '#3a7b7f' : '#999C9F',
      boxShadow: 'none',
    },
    textTransform: 'none',
  }}
>
  Submit
</Button>

                            </>
                        ) : (
                            <Box>
                                <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 16, fontWeight: 400, color: '#142434', mb: 3 }}>
                                    Thank you for submitting your feedback. Our team will reply to you shortly.
                                    <br />
                                    Would you like us to take you back to your{' '}
                                    <MuiLink
                                        component="button"
                                        onClick={() => navigate(-1)}
                                        sx={{
                                            color: '#489596',
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        previous page
                                    </MuiLink>
                                    ?
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* CheckHelp Section 40% */}
                    <Box sx={{ flex: '0 0 40%', display: 'flex', justifyContent: 'flex-end' }}>
                        <Box
                            sx={{
                                width: 332,
                                backgroundColor: '#489596',
                                borderRadius: '10px',
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                color: '#ffffff',
                            }}
                        >
                            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '28px', fontWeight: 400, mb: 2 }}>
                                Check Help?
                            </Typography>
                            <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: '16px', fontWeight: 400 }}>
                                Remember the answer you are looking for might be in our{' '}
                                <MuiLink component={Link} to="/help" sx={{ color: '#ffffff', textDecoration: 'underline' }}>
                                    Help Center
                                </MuiLink>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Footer />
        </>
    );
};

export default ContactUs;
